import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Chip, LinearProgress, Typography, Select, MenuItem } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import TabularStyles from '../styles/TabularStyles';
import useTemplateStyles from '../styles/TemplateStyles';
import UnderlineButton from '../../common/UnderlineTextButton ';
import Utils from '../../../common/utils';
import { getStatusStyles } from '../../../common/statusColorUtils';
import { getBrowserTimezone, formatDisplayTime, getTzLabelFromOptions } from '../../../common/dateTimeUtils';
import Icon from '../../icons/Icon';
import TooltipWrapper from '../../tooltip/TooltipWrapper';
import MathTextComponent from '../../common/MathTextComponent';
import InfoIconForError from '../../icons/InfoIconForError';

const TextTemplate = (props) => {
  const { text } = props;
  const classes = TabularStyles(props);

  return (
    <div className={`${classes.body2} ${classes.textPrimaryColor}`}>{text}</div>
  );
};

TextTemplate.propTypes = {
  text: PropTypes.string.isRequired
};

const BadgeTemplate = (props) => {
  const { topics, status, label, showIcon = true, count } = props;
  const classes = TabularStyles(props);


  const renderTopics = () => {
    if (topics) {
      const { backgroundColor, color } = getStatusStyles(status);

      return topics.map((topic, i) => (
        <Chip
          key={i}
          label={
            count > 0 ? (
              <MathTextComponent component="p" text={`${topic} (${count})`} className={classes.chipMathText} />
            ) : (
              <MathTextComponent component="p" text={topic} className={classes.chipMathText} />
            )
          }
          size="small"
          className={classes.chip}
          classes={{
            root: classes.badge,
            label: classes.chipLabel
          }}
          style={{ backgroundColor, borderRadius: 4, color }}
        />
      ));
    }

    return null;
  };

  const renderStatus = () => {
    if (label) {
      const {
        backgroundColor,
        color
      } = getStatusStyles(status);

      return (
        <Chip
          icon={showIcon ? (
            <div className={classes.dotIconContainer}>
              <Icon name="DotIcon" className={classes.dotIcon} style={{ color }} />
            </div>
          ) : null}
          label={label}
          size="small"
          classes={{
            root: classes.badge,
            label: classes.badgeLabel
          }}
          style={{ backgroundColor, color, borderRadius: 4 }}
        />
      );
    }

    return null;
  };

  return (
    <>
      {renderTopics()}
      {renderStatus()}
    </>
  );
};
BadgeTemplate.defaultProps = {
  count: 0
};

BadgeTemplate.propTypes = {
  topics: PropTypes.arrayOf(PropTypes.string).isRequired,
  status: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  showIcon: PropTypes.string.isRequired,
  count: PropTypes.number
};

const ProgressTemplate = (props) => {
  const { attempted, total, attemptedPercent } = props;
  const classes = TabularStyles(props);

  return (
    <div className={`${classes.primary} ${classes.primaryGap}`}>
      <Typography className={classes.body2} variant="body2">{`${attempted}/${total}`}</Typography>
      <LinearProgress
        className={classes.attemptedBar}
        variant="determinate"
        value={attemptedPercent}
      />
      <Typography className={`${classes.body2} ${classes.amethystColor}`} variant="body2">{`${attemptedPercent}%`}</Typography>
    </div>
  );
};

ProgressTemplate.propTypes = {
  attempted: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  attemptedPercent: PropTypes.number.isRequired
};

const OverviewTemplate = (props) => {
  const { name, subtitle, status, onClick, imageSrc, initials } = props;
  const classes = TabularStyles(props);

  const renderName = () => {
    if (imageSrc || initials) {
      return (
        <div className={classes.imageContainer}>
          {imageSrc
            ?
              <img src={imageSrc} alt={name} className={classes.image} />
            :
              <div className={classes.image}><span>{initials}</span></div>
          }
          <Typography className={classes.subtitle2} variant="subtitle2">{name}</Typography>
        </div>
      );
    }

    if (onClick) {
      return <UnderlineButton text={name} onClick={onClick} className={classes.linkButton} />;
    }

    return <MathTextComponent className={classes.subtitle2} variant="subtitle2" text={name} />;
  };

  return (
    <div className={classes.primary}>
      <div className={classes.primaryCol1}>
        {renderName()}
        <MathTextComponent className={classes.caption} variant="caption" text={subtitle} />
      </div>
      {status &&
      (
      <div>
        {status === 'success' && <CheckCircleIcon className={classes.checkIcon} />}
        {status === 'warning' && <ErrorIcon className={classes.errorIcon} />}
      </div>
      )}
    </div>
  );
};

OverviewTemplate.defaultProps = {
  status: null,
  onClick: null,
  imageSrc: null,
  initials: null
};

OverviewTemplate.propTypes = {
  name: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  status: PropTypes.string,
  onClick: PropTypes.func,
  imageSrc: PropTypes.string,
  initials: PropTypes.string
};

const TextChipTemplate = (props) => {
  const classes = useTemplateStyles(props);
  const {
    id,
    title,
    sections,
    label,
    onEditClick,
    showEditIcon,
    editIconTooltip,
    hasError,
    validationInfoToolTip
  } = props;

  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onEditClick(id);
  };

  return (
    <div className={classes.container}>
      <div className={classes.textContainer}>
        <div className={classes.titleWrapper}>
          <span className={classes.titleTextContainer}>
            <Typography id={`select-${Utils.sanitizeString(title)}`} className={`${classes.titleText} ${showEditIcon ? classes.titleMinWidth : null}`}>{title}</Typography>

            <div className={classes.editActionIconContainer}>
              {showEditIcon && (
                <TooltipWrapper tooltip={editIconTooltip} placement="top">
                  <IconButton
                    className={`${classes.editActionIconButton} ${hasError ? classes.editIconAlwaysVisible : 'editActionIcon'}`}
                    onClick={handleEditClick}
                  >
                    <Icon name="EditActionIcon" className={classes.titleText} />
                  </IconButton>
                </TooltipWrapper>
              )}
              {hasError && (
                <TooltipWrapper tooltip={validationInfoToolTip} placement="top">
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <InfoIconForError className={classes.errorInfoIcon} />
                  </span>
                </TooltipWrapper>
              )}
            </div>
          </span>
        </div>
        <div className={classes.sectionsContainer}>
          {label ? <span className={classes.sectionsLabel}>{label}:</span> : null}
          {sections ? sections.map((s, i) => (
            <span className={classes.sectionChip} key={i}>
              <span className={classes.chipContent}>
                <span className={classes.sectionNumber}>{s}</span>
              </span>
            </span>
          )) : null}
        </div>
      </div>
    </div>
  );
};

TextChipTemplate.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(PropTypes.string).isRequired,
  label: PropTypes.string.isRequired,
  onEditClick: PropTypes.func,
  showEditIcon: PropTypes.bool,
  editIconTooltip: PropTypes.string,
  hasError: PropTypes.bool,
  validationInfoToolTip: PropTypes.string
};
TextChipTemplate.defaultProps = {
  id: null,
  showEditIcon: false,
  onEditClick: () => {},
  editIconTooltip: null,
  hasError: false,
  validationInfoToolTip: null
};


const ContentTemplate = React.forwardRef(({ children }, ref) => {
  const classes = useTemplateStyles();

  return (
    <div className={classes.contentTemplate} ref={ref}>
      {children}
    </div>
  );
});

ContentTemplate.propTypes = {
  children: PropTypes.node.isRequired
};

const DateTemplate = ({ date, time, timeZoneOptions, info }) => {
  const classes = useTemplateStyles();

  const currentTimeZoneLabel = getTzLabelFromOptions(getBrowserTimezone(), timeZoneOptions);
  const formattedDate = Utils.getDate(date);
  const formattedTime = formatDisplayTime(date, time);
  // do not display timezone alone if datetime not present due to data
  const displayTime = formattedTime && currentTimeZoneLabel
    ? `${formattedTime} ${currentTimeZoneLabel}`
    : formattedTime;

  return (
    <ContentTemplate>
      <div className={classes.dateColumn}>
        <Info {...(info || {})} />
        <div>
          {!!formattedDate && (
            <Typography className={classes.sectionsLabel}>{formattedDate}</Typography>
          )}
          {!!displayTime && <Typography className={classes.sectionsLabel}>{displayTime}</Typography>}
        </div>
      </div>
    </ContentTemplate>
  );
};

DateTemplate.defaultProps = {
  time: null,
  timeZoneOptions: null,
  info: null
};

DateTemplate.propTypes = {
  date: PropTypes.string.isRequired,
  time: PropTypes.string,
  timeZoneOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    abbr: PropTypes.string,
    offset: PropTypes.string
  })),
  info: PropTypes.object
};

const ComprehensionTemplate = ({ label, type, bullet }) => {
  const classes = TabularStyles();
  const normalized = type
    ?.toLowerCase()
    .replace(/\s*\d+\/\d+\s*|\s+/g, '')
    .trim();

  const { backgroundColor, color } = getStatusStyles(normalized);
  const appliedStyle = { backgroundColor, color };

  return (
    <div className={`${classes.badge2} ${!bullet ? classes.extraPadding : ''}`} style={appliedStyle}>
      {bullet ?
        <div className={classes.dotIconContainer}>
          <Icon name="DotIcon" className={classes.dotIcon} style={{ color }} />
        </div> : null}
      <div className={`${classes.text}`}>{label}</div>
    </div>
  );
};

ComprehensionTemplate.defaultProps = {
  bullet: false
};

ComprehensionTemplate.propTypes = {
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  bullet: PropTypes.bool
};

const Info = (props) => {
  const classes = TabularStyles(props);
  const { icon, tooltip } = props;

  if (!icon || !tooltip) {
    return null;
  }

  return (
    <TooltipWrapper
      tooltip={tooltip}
      placement="bottom-end"
      classes={{
        tooltip: classes.infoTooltip
      }}
    >
      <div className={classes.infoIconContainer}>
        <Icon
          name={icon}
          className={classes.infoIcon}
        />
      </div>
    </TooltipWrapper>
  );
};

Info.propTypes = {
  icon: PropTypes.string,
  tooltip: PropTypes.string
};

Info.defaultProps = {
  icon: null,
  tooltip: null
};

const DropdownTemplate = (props) => {
  const classes = TabularStyles(props);
  const [open, setOpen] = useState(false);

  const {
    value,
    options,
    onChange,
    disabled = false,
    variant = 'text',
    statusBased = false,
    iconPosition
  } = props;

  const handleChange = (event) => {
    event.stopPropagation();
    event.preventDefault();
    onChange(event.target.value);
  };

  const renderValue = () => {
    if (variant === 'chip') {
      let style = {};

      if (statusBased) {
        const { backgroundColor, color } =
          getStatusStyles(value?.toLowerCase());
        style = { backgroundColor, color };
      }

      if (iconPosition === 'inside') {
        return (
          <Chip
            label={(
              <div className={classes.chipContent}>
                <span className={classes.chipText}>{value}</span>

                <ArrowDropDownIcon
                  className={`${classes.chipIcon} ${
                    open ? classes.rotateIcon : ''
                  }`}
                />
              </div>
          )}
            size="small"
            classes={{
              root: classes.badge,
              label: classes.badgeLabel
            }}
            style={{
              borderRadius: 4,
              ...style
            }}
          />
        );
      }

      return (
        <div className={classes.chipSelectContainer}>
          <Chip
            label={value}
            size="small"
            classes={{
              root: classes.badge,
              label: classes.badgeLabel
            }}
            style={{
              borderRadius: 4,
              ...style
            }}
          />

          <div className={classes.dropdownIconWrapper}>
            <ArrowDropDownIcon
              className={`${classes.dropdownIcon} ${
                open ? classes.rotateIcon : ''
              }`}
            />
          </div>
        </div>
      );
    }

    return (
      <Typography className={classes.body2}>
        {value}
      </Typography>
    );
  };

  return (
    <div className={classes.primary}>
      <Select
        value={value || ''}
        onChange={handleChange}
        disabled={disabled}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        variant="standard"
        disableUnderline
        renderValue={renderValue}
        IconComponent={variant === 'chip' ? () => null : undefined}
        classes={{
          select: classes.select,
          icon: classes.selectIcon
        }}
        MenuProps={{
          classes: {
            paper: classes.filterPopover
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          MenuListProps: {
            className: classes.menuList
          },
          getContentAnchorEl: null
        }}
      >
        {options.map((opt) => {
          const selected = value === opt.value;

          return (
            <MenuItem
              key={opt.value}
              value={opt.value}
              className={classes.filterMenuItem}
            >
              <div className={classes.filterMenuItemContent}>
                <Typography
                  className={
              selected
                ? classes.filterMenuItemSelectedText
                : classes.filterMenuItemText
            }
                >
                  {opt.label}
                </Typography>

                {selected && (
                <Icon name="TickIcon" className={classes.filterCheckIcon} />
                )}
              </div>
            </MenuItem>
          );
        })}
      </Select>
    </div>
  );
};

DropdownTemplate.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  statusBased: PropTypes.bool,
  iconPosition: PropTypes.string
};

DropdownTemplate.defaultProps = {
  value: '',
  disabled: false,
  variant: 'text',
  statusBased: false,
  iconPosition: 'outside'
};

export default DropdownTemplate;

export {
  OverviewTemplate,
  TextTemplate,
  TextChipTemplate,
  BadgeTemplate,
  ProgressTemplate,
  ContentTemplate,
  DateTemplate,
  ComprehensionTemplate,
  Info,
  DropdownTemplate
};
